import { useState } from "react";
import api from "../../lib/api";
import { StatePill } from "../CollaboratorBits";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import DeleteWithReasonDialog from "../DeleteWithReasonDialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Loader2, Pencil, Plus, Power, Trash2 } from "lucide-react";
import { useOrgMutation } from "./useOrgMutation";

const KIND_TEXT = {
  relation_type: { title: "Types de relation", singular: "type de relation", empty: "Aucun type de relation.", hint: "Salarié, prestataire, partenaire… Un type désactivé reste affiché sur les fiches qui l'utilisent mais n'est plus proposé." },
  zone: { title: "Zones", singular: "zone", empty: "Aucune zone pour le moment. Ajoutez par exemple vos régions ou villes de présence.", hint: "Les zones où les personnes interviennent." },
};

export default function ReferentialPanel({ kind, items, refs, canManage }) {
  const { busy, run } = useOrgMutation(refs);
  const { user } = useAuth();
  const canDelete = canManage && hasPermission(user, "services:delete");
  const [deleting, setDeleting] = useState(null);
  const text = KIND_TEXT[kind];
  const [editing, setEditing] = useState(null);
  const [label, setLabel] = useState("");
  const [toggling, setToggling] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false); // separate from `toggling`: the dialog keeps its text while it fades out

  const open = (item) => {
    setEditing(item);
    setLabel(item === "new" ? "" : item.label);
  };
  const save = async () => {
    const ok = editing === "new"
      ? await run(() => api.post("/org-referentials", { kind, label: label.trim() }), "Entrée ajoutée")
      : await run(() => api.patch(`/org-referentials/${editing.id}`, { expected_version: editing.version, label: label.trim() }), "Libellé mis à jour");
    if (ok) setEditing(null);
  };
  const toggle = async () => {
    const item = toggling;
    const ok = await run(() => api.patch(`/org-referentials/${item.id}`, { expected_version: item.version, active: !item.active }), item.active ? "Entrée désactivée" : "Entrée réactivée");
    if (ok) setConfirmOpen(false);
  };

  return (
    <div className="space-y-3" data-testid={`ref-panel-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">{text.hint}</p>
        {canManage && (
          <Button onClick={() => open("new")} className="shrink-0" data-testid={`btn-ref-new-${kind}`}>
            <Plus size={16} className="mr-1.5" /> Ajouter
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500" data-testid={`ref-empty-${kind}`}>{text.empty}</Card>
      ) : (
        <Card className="p-0 divide-y divide-slate-100">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3 p-4" data-testid={`ref-row-${item.code}`}>
              <div className="flex items-center gap-2 flex-wrap min-w-0"><span className="font-medium text-slate-900 break-words">{item.label}</span><StatePill active={item.active} /></div>
              {canManage && (
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" aria-label={`Renommer ${item.label}`} onClick={() => open(item)} data-testid={`btn-ref-edit-${item.code}`}><Pencil size={15} /></Button>
                  <Button variant="ghost" size="icon" aria-label={`${item.active ? "Désactiver" : "Réactiver"} ${item.label}`} onClick={() => { setToggling(item); setConfirmOpen(true); }} data-testid={`btn-ref-toggle-${item.code}`}><Power size={15} /></Button>
                  {canDelete && !item.is_system && <Button variant="ghost" size="icon" className="text-red-700" aria-label={`Supprimer ${item.label}`} onClick={() => setDeleting(item)} data-testid={`btn-ref-delete-${item.code}`}><Trash2 size={15} /></Button>}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      <DeleteWithReasonDialog
        target={deleting} testId="delete-ref" onClose={() => setDeleting(null)}
        title={`Supprimer ${text.singular} « ${deleting?.label || ""} » ?`}
        description="Elle va dans la corbeille et se restaure pendant 30 jours. Une entrée portée par des fiches, ou fournie avec l'application, ne peut pas être supprimée : désactivez-la à la place."
        onConfirm={async (reason) => {
          const target = deleting;
          setDeleting(null);
          await run(() => api.delete(`/org-referentials/${target.id}`, { data: { reason } }), "Mise à la corbeille");
        }}
      />
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing === "new" ? `Ajouter : ${text.singular}` : "Renommer"}</DialogTitle></DialogHeader>
          <div className="space-y-2"><Label htmlFor="ref-label">Libellé *</Label>
            <Input id="ref-label" className="text-base md:text-sm" maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="input-ref-label" /></div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Annuler</Button>
            <Button onClick={save} disabled={!label.trim() || busy} data-testid="btn-ref-save">
              {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{toggling?.active ? `Désactiver « ${toggling?.label} » ?` : `Réactiver « ${toggling?.label} » ?`}</DialogTitle>
            <DialogDescription>
              {toggling?.active
                ? "Les fiches qui l'utilisent déjà le conservent, mais il ne sera plus proposé pour de nouvelles affectations."
                : "Il pourra de nouveau être attribué."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={busy}>Annuler</Button>
            <Button onClick={toggle} disabled={busy} data-testid="btn-ref-toggle-confirm">
              {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} {toggling?.active ? "Désactiver" : "Réactiver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
